import { PageHeader } from "@/components/ui/design-system";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { BlokadaOverrideDialog } from "@/components/BlokadaOverrideDialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import {
  dohvatiObvezeTvrtke,
  type ObvezaTvrtke,
} from "@/lib/companyObligations";
import {
  dohvatiRokove,
  dohvatiStatusePodobnosti,
  type Rok,
  type SkupnaPodobnost,
} from "@/lib/eligibility";
import { cijeliBroj } from "@/lib/format";
import {
  dohvatiRasporedeZaOdobrenje,
  odobriBlokaduOverride,
  type RasporedZaOdobrenje,
} from "@/lib/gradilista";
import { porukaDohvata } from "@/lib/greske";
import { prebroj } from "@/lib/paznja";
import { dohvatiRadnike, type Radnik } from "@/lib/radnici";
import { cn } from "@/lib/utils";

const CIJELA_EVIDENCIJA = 200;
const PROZOR_ROKOVA_DANA = 120;

interface PodaciUprave {
  radnici: Radnik[];
  podobnosti: SkupnaPodobnost[];
  rokovi: Rok[];
  obvezeTvrtke: ObvezaTvrtke[];
}

export function PocetnaUpravaPage() {
  const [podaci, setPodaci] = useState<PodaciUprave | null>(null);
  const [zaOdobrenje, setZaOdobrenje] = useState<
    RasporedZaOdobrenje[] | null
  >(null);
  const [odabrani, setOdabrani] = useState<RasporedZaOdobrenje | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    const kontroler = new AbortController();

    Promise.all([
      dohvatiRasporedeZaOdobrenje(kontroler.signal),
      dohvatiRadnike(
        { limit: CIJELA_EVIDENCIJA, samo_aktivni: true },
        kontroler.signal,
      ),
      dohvatiStatusePodobnosti(kontroler.signal),
      dohvatiRokove(PROZOR_ROKOVA_DANA, kontroler.signal),
      dohvatiObvezeTvrtke(kontroler.signal),
    ])
      .then(([rasporedi, radnici, podobnosti, rokovi, obvezeTvrtke]) => {
        if (kontroler.signal.aborted) return;
        setZaOdobrenje(rasporedi);
        setPodaci({
          radnici: radnici.items,
          podobnosti,
          rokovi: rokovi.filter(
            (rok) =>
              rok.source !== "company_obligation" &&
              rok.source !== "site_obligation",
          ),
          obvezeTvrtke,
        });
      })
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska(porukaDohvata(error));
        console.error(error);
      });

    return () => kontroler.abort();
  }, []);

  async function odobri(razlog: string): Promise<void> {
    if (!odabrani) return;
    await odobriBlokaduOverride(odabrani.id, razlog);
    setZaOdobrenje((trenutni) =>
      trenutni?.filter((zapis) => zapis.id !== odabrani.id) ?? null,
    );
    setOdabrani(null);
  }

  const naslov = (
    <PageHeader title={<>Uprava</>} />
  );

  if (greska) {
    return (
      <div className="flex flex-col gap-8">
        {naslov}
        <EmptyState naslov="Početna se ne može učitati" objasnjenje={greska} />
      </div>
    );
  }

  if (!podaci || !zaOdobrenje) {
    return (
      <div className="flex flex-col gap-8">
        {naslov}
        <SkeletonTablica redaka={4} />
      </div>
    );
  }

  const aktivni = podaci.radnici.filter((radnik) => radnik.aktivan !== false).length;
  const podobni = podaci.podobnosti.filter(
    (radnik) => radnik.eligibility.status === "podoban",
  ).length;
  const kriticno = prebroj(podaci.radnici).kriticno;
  const rokoviIstekli = podaci.rokovi.filter(
    (rok) => rok.days_until_due < 0,
  ).length;
  const rokoviUskoro = podaci.rokovi.filter(
    (rok) => rok.days_until_due >= 0,
  ).length;
  const obvezeIstekle = podaci.obvezeTvrtke.filter(
    (obveza) => obveza.deadline_status === "expired",
  ).length;
  const obvezeUpozorenje = podaci.obvezeTvrtke.filter(
    (obveza) => obveza.deadline_status === "warning",
  ).length;

  return (
    <div className="flex flex-col gap-8">
      {naslov}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Brojka
          naziv="Čeka odobrenje"
          vrijednost={zaOdobrenje.length}
          poveznica="#za-odobrenje"
          kriticna={zaOdobrenje.length > 0}
        />
        <Brojka
          naziv="Aktivni radnici"
          vrijednost={aktivni}
          poveznica="/zaposlenici"
        />
        <Brojka
          naziv="Podobni"
          vrijednost={podobni}
          poveznica="/rokovi-i-obveze"
        />
        <Brojka
          naziv="Kritična upozorenja"
          vrijednost={kriticno}
          poveznica="/rokovi-i-obveze"
          kriticna={kriticno > 0}
        />
      </section>

      <section
        id="za-odobrenje"
        aria-labelledby="za-odobrenje-naslov"
        className="avm-surface scroll-mt-6 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6"
      >
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="oznaka">ZA ODOBRENJE</p>
            <h2
              id="za-odobrenje-naslov"
              className="mt-1 text-[var(--text-section)] font-semibold"
            >
              Zahtjevi za iznimku
            </h2>
          </div>
          <span className="broj text-[var(--text-card)] font-bold">
            {cijeliBroj(zaOdobrenje.length)}
          </span>
        </div>

        {zaOdobrenje.length === 0 ? (
          <div className="rounded-[var(--radius-card)] border border-[var(--color-status-ok-fg)]/20 bg-[var(--color-status-ok-bg)] p-5 text-[var(--color-status-ok-fg)]">
            <p className="font-medium">Nema zahtjeva koji čekaju odobrenje</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {zaOdobrenje.map((zapis) => (
              <li
                key={zapis.id}
                className="grid gap-4 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-bad-row)] p-4 lg:grid-cols-[1.2fr_1fr_1.6fr_auto] lg:items-center"
              >
                <div>
                  <Link
                    to={`/zaposlenici/${zapis.radnik_id}`}
                    className="font-semibold underline underline-offset-2"
                  >
                    {zapis.radnik ?? "Nepoznat radnik"}
                  </Link>
                  <p className="mt-1 text-[var(--text-small)] text-[var(--color-muted-foreground)]">
                    {vrijemeCekanja(zapis.requested_at)}
                  </p>
                </div>
                <p>{zapis.gradiliste ?? "Nepoznato gradilište"}</p>
                <div>
                  <p className="oznaka">Razlozi nepodobnosti</p>
                  <p className="mt-1 text-[var(--text-small)] text-[var(--color-status-bad-fg)]">
                    {(zapis.blocking_reasons ?? []).join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <Button variant="outline" onClick={() => setOdabrani(zapis)}>
                    Odobri iznimku
                  </Button>
                  {/* Management has no roster-update grant in the matrix. */}
                  <Link
                    to="/gradilista"
                    className="font-medium underline underline-offset-2"
                  >
                    Otvori Gradilišta
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="sazetak-upozorenja" className="flex flex-col gap-3">
        <h2 id="sazetak-upozorenja" className="text-[var(--text-section)] font-semibold">
          Sažetak upozorenja
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <SazetakUpozorenja
            naziv="Rokovi radnika"
            poveznica="/rokovi-i-obveze"
            stavke={[
              ["Isteklo u zadnjih 120 dana", rokoviIstekli],
              ["U idućih 120 dana", rokoviUskoro],
            ]}
          />
          <SazetakUpozorenja
            naziv="Obveze tvrtke"
            poveznica="/obveze-tvrtke"
            stavke={[
              ["Isteklo", obvezeIstekle],
              ["Upozorenje", obvezeUpozorenje],
            ]}
          />
        </div>
      </section>

      {odabrani ? (
        <BlokadaOverrideDialog
          raspored={odabrani}
          onOdustani={() => setOdabrani(null)}
          onPotvrdi={odobri}
        />
      ) : null}
    </div>
  );
}

function Brojka({
  naziv,
  vrijednost,
  poveznica,
  kriticna = false,
}: {
  naziv: string;
  vrijednost: number;
  poveznica: string;
  kriticna?: boolean;
}) {
  const className = cn(
    "block rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6 transition-colors hover:border-[var(--color-primary)]",
    kriticna && "bg-[var(--color-status-bad-bg)]",
  );
  const sadrzaj = (
    <>
      <p className="oznaka">{naziv}</p>
      <p
        className={cn(
          "broj mt-2 text-[length:var(--text-title)] font-bold",
          kriticna && "text-[var(--color-status-bad-fg)]",
        )}
      >
        {cijeliBroj(vrijednost)}
      </p>
    </>
  );

  return poveznica.startsWith("#") ? (
    <a href={poveznica} className={className}>
      {sadrzaj}
    </a>
  ) : (
    <Link to={poveznica} className={className}>
      {sadrzaj}
    </Link>
  );
}

function SazetakUpozorenja({
  naziv,
  poveznica,
  stavke,
}: {
  naziv: string;
  poveznica: string;
  stavke: [string, number][];
}) {
  return (
    <Link
      to={poveznica}
      className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5 hover:border-[var(--color-primary)]"
    >
      <p className="font-semibold">{naziv}</p>
      <div className="mt-3 flex flex-wrap gap-6">
        {stavke.map(([oznaka, vrijednost]) => (
          <div key={oznaka}>
            <p className="broj text-[var(--text-card)] font-bold">
              {cijeliBroj(vrijednost)}
            </p>
            <p className="text-[var(--text-small)] text-[var(--color-muted-foreground)]">
              {oznaka}
            </p>
          </div>
        ))}
      </div>
    </Link>
  );
}

function vrijemeCekanja(requestedAt: string, sada = new Date()): string {
  const pocetak = new Date(requestedAt);
  const dana = Math.max(
    0,
    Math.floor((sada.getTime() - pocetak.getTime()) / (24 * 60 * 60 * 1000)),
  );
  if (dana === 0) return "Čeka manje od jednog dana";
  if (dana === 1) return "Čeka 1 dan";
  return `Čeka ${dana} dana`;
}
